import { Formik } from "formik";
import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { InputWithErrorMessage } from "components/InputWithErrorMessage";
import { LoadingSpinner } from "components/LoadingSpinner";
import { Note } from "components/Note/Note";
import { Heading4, Heading5 } from "styles/elements/Headings";

import { useAdmin } from "hooks/useAdmin";
import { useReactTooltip } from "hooks/useReactTooltip";
import type { SchoolResponse } from "models/admin/Admin";

import { type SchoolUpdateFormFields, buildUpdateFormSchema } from "./SchoolUpdateForm.schema";

import { modalIds } from "constants/modals-constants";
import { useModal } from "hooks/useModal";
import { TransferUsersModal } from "../TransferUsersModal/TransferUsersModal";

import { Button } from "components/Button";
import { EncircledIcon } from "components/EncircledIcon";
import { ADMIN_ENVIRONMENT_ROUTES } from "constants/routes";
import { useCurrentPathMatch } from "hooks/useCurrentPathMatch";
import { SchoolType } from "models/auth/Login";
import { ParagraphSmall } from "styles/elements/Texts";
import { Box, CssFlex, CssGrid } from "styles/helpers/layout";
import {
  StyledBasicInfo,
  StyledButton,
  StyledEducationHeader,
  StyledEducationOptionBox,
  StyledField,
  StyledForm,
  StyledLoadingWrapper,
  StyledPageTitle,
  StyledSchoolyearWrapper,
  StyledToggleSwitch,
  StyledUserCountInput,
} from "./SchoolUpdateForm.styled";

interface PathParams {
  schoolId: string;
}

export const SchoolUpdateForm: React.FC = observer(() => {
  const { t } = useTranslation("admin-environment");
  const {
    fetchSchoolDetails,
    schoolDetails: school,
    setSchoolDetails,
    createSchool,
    updateSchool,
  } = useAdmin();
  const { schoolId } = useParams() as unknown as PathParams;
  const navigate = useNavigate();
  const { currentRouteMatches } = useCurrentPathMatch();
  const { openModal } = useModal();

  const isSchool =
    school.type === SchoolType.SCHOOL ||
    (schoolId === "new" && currentRouteMatches(ADMIN_ENVIRONMENT_ROUTES.SCHOOL_CREATE));

  const isEditForm = schoolId !== "new";

  useReactTooltip(Object.keys(school).length > 0);

  useEffect(() => {
    if (isEditForm) {
      fetchSchoolDetails(schoolId);
    }
    return () => setSchoolDetails({} as SchoolResponse);
  }, [isEditForm, schoolId, setSchoolDetails, fetchSchoolDetails]);

  const refreshSchoolDetails = () => {
    fetchSchoolDetails(schoolId);
  };

  const onFormSubmit = (values: SchoolUpdateFormFields) => {
    const commonPayload = {
      name: values.title,
      domain: values.domain,
      educations: values.educations.map((education) => ({
        id: education.id || undefined,
        name: education.title,
        archived: education.archived,
        isSignpostDefault: education.isSignpostDefault,
        initProgressFromIntakeTestEnabled: education.initProgressFromIntakeTestEnabled,
      })),
      immersiveReaderEnabled: values.immersiveReaderEnabled,
      signpostLoginEnabled: values.signpostLoginEnabled,
      sebEnabled: values.sebEnabled,
      schoolyearKey: values.schoolyearEnabled ? values.schoolyearKey : null,
      type: school?.type || (isSchool ? SchoolType.SCHOOL : SchoolType.COMPANY),
    };

    // Call different endpoint depending on whether we're creating a new school
    // or updating an existing one
    if (schoolId === "new") {
      return createSchool(commonPayload).then(() => {
        navigate(
          isSchool ? ADMIN_ENVIRONMENT_ROUTES.SCHOOL_LIST : ADMIN_ENVIRONMENT_ROUTES.COMPANY_LIST,
        );
      });
    }

    return updateSchool(school.id, commonPayload);
  };

  if (Object.keys(school).length > 0 || schoolId === "new") {
    return (
      <>
        <StyledPageTitle>
          {isSchool
            ? t("schoolDetails.pageTitleSchool", "School details")
            : t("schoolDetails.pageTitleOrganization", "Organization details")}
        </StyledPageTitle>
        <ParagraphSmall>
          {isSchool
            ? t("schoolDetails.pageDescriptionSchool", "Edit school details")
            : t("schoolDetails.pageDescriptionOrganization", "Edit organization details")}
        </ParagraphSmall>
        <Formik
          enableReinitialize
          initialValues={{
            id: school.id,
            title: school.title || "",
            domain: school.domain || "",
            educations:
              school?.educations?.map((education) => ({
                id: education.id,
                title: education.title,
                archived: education.archived,
                isSignpostDefault: !!education.isSignpostDefault,
                initProgressFromIntakeTestEnabled: !!education.initProgressFromIntakeTestEnabled,
                linkedUserCount: education.linkedUserCount || 0,
              })) || [],
            immersiveReaderEnabled:
              school.immersiveReaderEnabled === false ? school.immersiveReaderEnabled : true,
            sebEnabled: school.sebEnabled || false,
            schoolyearEnabled: !!school.schoolyearKey || false,
            schoolyearKey: school.schoolyearKey || "",
            signpostLoginEnabled: !!school.signpostLoginEnabled,
          }}
          validationSchema={buildUpdateFormSchema()}
          onSubmit={onFormSubmit}
        >
          {(formik) => (
            <StyledForm noValidate onSubmit={formik.handleSubmit}>
              <Heading4>
                {isSchool
                  ? t("schoolDetails.form.basicInfoHeaderSchool", "Basic information")
                  : t("schoolDetails.form.basicInfoHeaderOrganization", "Organization information")}
              </Heading4>
              <StyledBasicInfo>
                <StyledField>
                  <label htmlFor="school-admin-edit-form-title">
                    {t("schoolDetails.form.name.label", "Name")}
                  </label>
                  <InputWithErrorMessage
                    dataCy="school-name"
                    errorMessage={formik.errors.title}
                    hasError={!!(formik.errors.title && formik.touched.title)}
                    id="school-admin-edit-form-title"
                    name="title"
                    value={formik.values.title}
                    onChange={formik.handleChange}
                  />
                </StyledField>
                <StyledField>
                  <label htmlFor="school-admin-edit-form-domain">
                    {t("schoolDetails.form.domain.label", "Domain")}
                  </label>
                  <InputWithErrorMessage
                    dataCy="school-domain"
                    errorMessage={formik.errors.domain}
                    hasError={!!(formik.errors.domain && formik.touched.domain)}
                    id="school-admin-edit-form-domain"
                    name="domain"
                    value={formik.values.domain}
                    onChange={formik.handleChange}
                  />
                </StyledField>
              </StyledBasicInfo>

              {/* -- Informative message on Signpost -- */}
              {formik.values.signpostLoginEnabled && (
                <Box paddingTop={4}>
                  <Note dataCy="signpost-enabled-message" variant="info">
                    {t("schoolDetails.signpostActiveInfo")}
                  </Note>
                </Box>
              )}

              <StyledField data-cy="school-educations">
                <StyledEducationHeader>
                  {isSchool
                    ? t("schoolDetails.form.educationsHeader", "Educations")
                    : t("schoolDetails.form.departmentsHeader", "Departments")}
                </StyledEducationHeader>

                {formik.errors.educations && (
                  <Note dataCy="educations-validation-errors" variant="error">
                    {formik.errors.educations.toString()}
                  </Note>
                )}

                {formik.values.educations.map((education, index) => (
                  <CssGrid
                    key={`${education.id || index}`}
                    data-cy={`education-row-${index}`}
                    gridTemplateColumns={isEditForm ? "1fr 7rem" : "1fr"}
                    alignItems="center"
                    columnGap={1}
                  >
                    <CssGrid columnGap={1} gridTemplateColumns={isEditForm ? "1fr 7rem" : "1fr"}>
                      <StyledField>
                        <label htmlFor={`school-admin-edit-form-education-${index}-name`}>
                          {isSchool
                            ? t("schoolDetails.form.education.name.label", "Education")
                            : t("schoolDetails.form.department.name.label", "Department")}
                        </label>
                        <InputWithErrorMessage
                          dataCy="education-name-input"
                          errorMessage={t("schoolDetails.form.education.name.errors.required")}
                          hasError={education.title === ""}
                          disabled={education.archived}
                          id={`school-admin-edit-form-education-${index}-name`}
                          name="education"
                          value={education.title}
                          onChange={(e) => {
                            formik.setFieldValue(
                              "educations",
                              formik.values.educations.map((item, i) => {
                                if (index === i) {
                                  return {
                                    ...item,
                                    title: e.target.value,
                                  };
                                }
                                return item;
                              }),
                            );
                          }}
                        />
                      </StyledField>
                      {/* Only display user count when editing existing schools */}
                      {isEditForm && (
                        <StyledField>
                          <label htmlFor={`school-admin-edit-form-education-${index}-user-count`}>
                            {t("schoolDetails.form.education.userCount.label")}
                          </label>
                          <StyledUserCountInput
                            readOnly
                            dataCy="education-user-count-input"
                            id={`school-admin-edit-form-education-${index}-user-count`}
                            name="linkedUserCount"
                            type="number"
                            value={education.linkedUserCount || 0}
                          />
                        </StyledField>
                      )}
                    </CssGrid>
                    {/* Additional action buttons to show when editing an existing school */}
                    {isEditForm && (
                      <CssFlex
                        style={education.title ? { marginTop: "3rem" } : {}}
                        flexDirection="row"
                        justifyContent="flex-end"
                        gap={1}
                      >
                        {/* Allow transferring users only for educations that have members */}
                        {education.id && education.linkedUserCount > 0 && (
                          <Button
                            aria-label={t("schoolDetails.form.education.transferUsers.label")}
                            dataCy="education-transfer-users-button"
                            data-tip={t("schoolDetails.form.education.transferUsers.label")}
                            id={`school-admin-edit-form-education-${index}-transfer-users-button`}
                            onClick={(e) => {
                              e.preventDefault();
                              openModal(modalIds.transferUsersToAnotherEducationModal, {
                                educationId: education.id,
                                educationTitle: education.title,
                              });
                            }}
                          >
                            <EncircledIcon iconName="fas fa-user-chart" variant="primary" />
                          </Button>
                        )}
                        {/* Allow archiving / restoring educations */}
                        {!education.archived ? (
                          <Button
                            aria-label={t("schoolDetails.form.education.archive.label")}
                            dataCy="education-archive-button"
                            data-tip={t("schoolDetails.form.education.archive.label")}
                            id={`school-admin-edit-form-education-${index}-archive-button`}
                            onClick={(e) => {
                              e.preventDefault();
                              formik.setFieldValue(
                                "educations",
                                formik.values.educations.map((item, i) => {
                                  if (index === i) {
                                    return {
                                      ...item,
                                      archived: true,
                                    };
                                  }
                                  return item;
                                }),
                              );
                            }}
                          >
                            <EncircledIcon iconName="fas fa-archive" variant="secondary" />
                          </Button>
                        ) : (
                          <Button
                            aria-label={t("schoolDetails.form.education.restore.label")}
                            dataCy="education-restore-button"
                            data-tip={t("schoolDetails.form.education.restore.label")}
                            id={`school-admin-edit-form-education-${index}-restore-button`}
                            onClick={(e) => {
                              e.preventDefault();
                              formik.setFieldValue(
                                "educations",
                                formik.values.educations.map((item, i) => {
                                  if (index === i) {
                                    return {
                                      ...item,
                                      archived: false,
                                    };
                                  }
                                  return item;
                                }),
                              );
                            }}
                          >
                            <EncircledIcon iconName="fas fa-undo" variant="default" />
                          </Button>
                        )}
                      </CssFlex>
                    )}
                    {/* Config at education level */}
                    {isSchool && (
                      <StyledEducationOptionBox
                        padding="1.5rem 1rem 0 1rem"
                        style={{ gridColumn: "1" }}
                      >
                        <Heading5>
                          {t("schoolDetails.form.settingsHeaderLabel", "Settings")}
                        </Heading5>
                        {/* -- Toggle to set education as Signpost default -- */}
                        {formik.values.signpostLoginEnabled && (
                          <StyledToggleSwitch
                            dataCy="toggle-education-signpost-default"
                            isActive={!!education.isSignpostDefault}
                            labelSubtitle={t(
                              "schoolDetails.form.education.isSignpostDefaultToggle.label",
                            )}
                            name="isSignpostDefault"
                            variant="primary"
                            onChange={(e) => {
                              formik.setFieldValue(
                                "educations",
                                formik.values.educations.map((item, i) => {
                                  // Update all educations if flag was enabled, as only one
                                  // can be set as default
                                  if (e.target.checked) {
                                    return {
                                      ...item,
                                      isSignpostDefault: index === i,
                                    };
                                  }

                                  // If flag was disabled, we don't need to make modifications in other entries
                                  if (index === i) {
                                    return {
                                      ...item,
                                      isSignpostDefault: e.target.checked,
                                    };
                                  }
                                  return item;
                                }),
                              );
                            }}
                          />
                        )}
                        {/* -- Permissions -- */}
                        <StyledToggleSwitch
                          dataCy="toggle-education-init-progress-from-intake-test-enabled"
                          isDisabled={education.archived}
                          isActive={education.initProgressFromIntakeTestEnabled || false}
                          labelSubtitle={t(
                            "schoolDetails.form.initProgressFromIntakeTestToggle.label",
                          )}
                          name="initProgressFromIntakeTestEnabled"
                          variant="primary"
                          onChange={(e) => {
                            formik.setFieldValue(
                              "educations",
                              formik.values.educations.map((item, i) => {
                                if (index === i) {
                                  return {
                                    ...item,
                                    initProgressFromIntakeTestEnabled: e.target.checked,
                                  };
                                }
                                return item;
                              }),
                            );
                          }}
                        />
                      </StyledEducationOptionBox>
                    )}
                  </CssGrid>
                ))}

                <StyledButton
                  dataCy="add-education"
                  variant="default"
                  onClick={(event) => {
                    const { setFieldValue, values } = formik;
                    event.preventDefault();
                    setFieldValue("educations", [
                      ...values.educations,
                      {
                        id: null,
                        title: "",
                        isSignpostDefault: false,
                        initProgressFromIntakeTestEnabled: false,
                      },
                    ]);
                  }}
                >
                  {isSchool
                    ? t("schoolDetails.form.addEducationButton.labelSchool", "+ Add education")
                    : t(
                        "schoolDetails.form.addEducationButton.labelOrganization",
                        "+ Add department",
                      )}
                </StyledButton>
              </StyledField>

              {/* --- Additional settings --- */}
              <Heading4>{t("schoolDetails.form.settingsHeaderLabel", "Settings")}</Heading4>
              <StyledToggleSwitch
                dataCy="toggle-immersive-reader-enabled"
                isActive={formik.values.immersiveReaderEnabled}
                labelSubtitle={t("schoolDetails.form.irToggle.label", "Immersive reader available")}
                name="immersiveReaderEnabled"
                variant="primary"
                onChange={formik.handleChange}
              />
              {isSchool && (
                <>
                  <StyledToggleSwitch
                    dataCy="toggle-signpost-login-enabled"
                    isActive={formik.values.signpostLoginEnabled}
                    labelSubtitle={t(
                      "schoolDetails.form.signpostToggle.label",
                      "Signpost login enabled",
                    )}
                    name="signpostLoginEnabled"
                    variant="primary"
                    onChange={(e) => {
                      formik.handleChange(e);

                      // If we disable Signpost for the school, automatically set default
                      // signpost flag to false on every education
                      if (!e.target.checked) {
                        formik.setFieldValue(
                          "educations",
                          formik.values.educations.map((item) => ({
                            ...item,
                            isSignpostDefault: false,
                          })),
                        );

                        // Force validation to ensure Signpost default error doesn't wrongly appear
                        // after resetting the default flag for all educations, as it seems like Yup
                        // is not automatically recalculating the conditional schema when the signpost
                        // enabled flag value changes
                        formik.validateForm();
                      }
                    }}
                  />
                  <StyledToggleSwitch
                    dataCy="toggle-seb-enabled"
                    isActive={formik.values.sebEnabled || false}
                    labelSubtitle={t("schoolDetails.form.sebToggle.label", "SEB enabled")}
                    name="sebEnabled"
                    variant="primary"
                    onChange={formik.handleChange}
                  />
                  <StyledToggleSwitch
                    dataCy="toggle-schoolyear-enabled"
                    isActive={formik.values.schoolyearEnabled || false}
                    labelSubtitle={t(
                      "schoolDetails.form.schoolyearToggle.label",
                      "Schoolyear enabled",
                    )}
                    name="schoolyearEnabled"
                    variant="primary"
                    onChange={formik.handleChange}
                  />
                  {formik.values.schoolyearEnabled && (
                    <StyledSchoolyearWrapper>
                      <label htmlFor="school-admin-edit-form-domain">
                        {t("schoolDetails.form.schoolyearKey.label", "Domain")}
                      </label>
                      <InputWithErrorMessage
                        dataCy="schoolyear-key"
                        errorMessage={formik.errors.schoolyearKey}
                        hasError={!!(formik.errors.schoolyearKey && formik.touched.schoolyearKey)}
                        id="school-admin-edit-form-schoolyear-key"
                        name="schoolyearKey"
                        value={formik.values.schoolyearKey}
                        onChange={formik.handleChange}
                      />
                    </StyledSchoolyearWrapper>
                  )}
                </>
              )}

              <StyledButton
                dataCy="save-school-button"
                disabled={formik.isSubmitting}
                type="submit"
                variant="primary"
              >
                {t("schoolDetails.form.saveButton.label", "Save")}
              </StyledButton>
            </StyledForm>
          )}
        </Formik>
        {/* -- Render transfer user modal only when updating existing schools -- */}
        {isEditForm && <TransferUsersModal onTransferCompleted={refreshSchoolDetails} />}
      </>
    );
  }

  return (
    <StyledLoadingWrapper>
      <LoadingSpinner />
    </StyledLoadingWrapper>
  );
});
